/* global $ */
import React, { useState, useEffect } from 'react';
import axiosInstance from "../axiosInstance";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import Footer from '../components/Footer';

function VendorContact() {
    const navigate = useNavigate();
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);

    // Table state
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(100);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    // Advanced filters
    const [filters, setFilters] = useState({
        name: '',
        phone: '',
        email: '',
        designation: '',
        status: ''
    });

    // ─── Filter & Sort ────────────────────────────────────────────────────────────

    const filteredContacts = contacts.filter(c =>
        c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.phone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.designation?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const advancedFilteredContacts = filteredContacts.filter(c => {
        return (
            (filters.name === '' || c.name?.toLowerCase().includes(filters.name.toLowerCase())) &&
            (filters.phone === '' || c.phone?.includes(filters.phone)) &&
            (filters.email === '' || c.email?.toLowerCase().includes(filters.email.toLowerCase())) &&
            (filters.designation === '' || c.designation?.toLowerCase().includes(filters.designation.toLowerCase())) &&
            (filters.status === '' || String(c.isactive) === filters.status)
        );
    });

    const sortedContacts = React.useMemo(() => {
        let sortable = [...advancedFilteredContacts];
        if (sortConfig.key) {
            sortable.sort((a, b) => {
                let aVal = a[sortConfig.key] ?? '';
                let bVal = b[sortConfig.key] ?? '';
                if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return sortable;
    }, [advancedFilteredContacts, sortConfig]);

    // Pagination
    const totalItems = sortedContacts.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentContacts = sortedContacts.slice(startIndex, endIndex);

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
        setSortConfig({ key, direction });
    };

    const getSortIcon = (key) => {
        if (sortConfig.key === key) return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
        return '';
    };

    useEffect(() => { setCurrentPage(1); }, [searchTerm, filters]);

    // ─── API ─────────────────────────────────────────────────────────────────────

    const fetchContacts = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get("/api/vendorsContact");
            setContacts(response.data.data || response.data);
        } catch (error) {
            console.error("Error fetching vendor contacts:", error);
            Swal.fire({ icon: 'error', title: 'Error!', text: 'Failed to fetch vendor contacts' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchContacts(); }, []);

    // ─── Handlers ────────────────────────────────────────────────────────────────

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const clearFilters = () => {
        setFilters({ name: '', phone: '', email: '', designation: '', status: '' });
        setSearchTerm('');
    };

    const handleViewDetails = (contactId) => {
        navigate(`/vendor-contact-details/${contactId}`);
    };

    const handleDelete = async (contactId) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                await axiosInstance.delete(`/api/vendorsContact/${contactId}`);
                Swal.fire({ icon: 'success', title: 'Deleted!', text: 'Contact has been deleted.', timer: 1500, showConfirmButton: false });
                fetchContacts();
            } catch (error) {
                Swal.fire({ icon: 'error', title: 'Error!', text: 'Failed to delete contact' });
            }
        }
    };

    // ─── Pagination Component ─────────────────────────────────────────────────────

    const Pagination = () => {
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
        if (endPage - startPage + 1 < maxVisiblePages) startPage = Math.max(1, endPage - maxVisiblePages + 1);

        const pageNumbers = [];
        for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);

        return (
            <div className="d-flex justify-content-between align-items-center mt-3">
                <div>
                    Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} entries
                    {filteredContacts.length !== contacts.length && ` (filtered from ${contacts.length} total entries)`}
                </div>
                <nav>
                    <ul className="pagination mb-0">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
                                <i className="fa fa-angle-double-left me-1"></i> First
                            </button>
                        </li>
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1}>
                                Previous
                            </button>
                        </li>
                        {pageNumbers.map(n => (
                            <li key={n} className={`page-item ${n === currentPage ? 'active' : ''}`}>
                                <button className="page-link" onClick={() => setCurrentPage(n)}>{n}</button>
                            </li>
                        ))}
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages}>
                                Next
                            </button>
                        </li>
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>
                                Last <i className="fa fa-angle-double-right ms-1"></i>
                            </button>
                        </li>
                    </ul>
                </nav>
            </div>
        );
    };

    // ─── Render ───────────────────────────────────────────────────────────────────

    return (
        <div>
            <div className="page-wrapper">
                <div className="content container-fluid">

                    {/* Page Header */}
                    <div className="page-header">
                        <div className="content-page-header">
                            <h5>Vendor Contacts</h5>
                            <div className="list-btn">
                                <ul className="filter-list">
                                    <li>
                                        <button
                                            className="btn btn-filters w-auto"
                                            onClick={() => $('#filter_inputs').toggle()}
                                        >
                                            <span className="me-2">
                                                <img src="assets/img/icons/filter-icon.svg" alt="filter" />
                                            </span>Filter
                                        </button>
                                    </li>
                                    <li>
                                        <div className="dropdown dropdown-action">
                                            <a href="#" className="btn-filters" data-bs-toggle="dropdown" aria-expanded="false">
                                                <span><i className="fe fe-download"></i></span>
                                            </a>
                                            <div className="dropdown-menu dropdown-menu-end">
                                                <ul className="d-block">
                                                    <li>
                                                        <a className="d-flex align-items-center download-item" href="javascript:void(0);">
                                                            <i className="far fa-file-pdf me-2"></i>PDF
                                                        </a>
                                                    </li>
                                                    <li>
                                                        <a className="d-flex align-items-center download-item" href="javascript:void(0);">
                                                            <i className="far fa-file-text me-2"></i>CSV
                                                        </a>
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    </li>
                                    <li>
                                        <a className="btn-filters" href="javascript:void(0);" title="Print">
                                            <span><i className="fe fe-printer"></i></span>
                                        </a>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Search Filter */}
                    <div id="filter_inputs" className="card filter-card" style={{ display: 'none' }}>
                        <div className="card-body pb-0">
                            <div className="row">
                                <div className="col-sm-6 col-md-3">
                                    <div className="input-block mb-3">
                                        <label>Name</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="name"
                                            value={filters.name}
                                            onChange={handleFilterChange}
                                            placeholder="Search by name"
                                        />
                                    </div>
                                </div>
                                <div className="col-sm-6 col-md-3">
                                    <div className="input-block mb-3">
                                        <label>Phone</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="phone"
                                            value={filters.phone}
                                            onChange={handleFilterChange}
                                            placeholder="Search by phone"
                                        />
                                    </div>
                                </div>
                                <div className="col-sm-6 col-md-3">
                                    <div className="input-block mb-3">
                                        <label>Email</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="email"
                                            value={filters.email}
                                            onChange={handleFilterChange}
                                            placeholder="Search by email"
                                        />
                                    </div>
                                </div>
                                <div className="col-sm-6 col-md-3">
                                    <div className="input-block mb-3">
                                        <label>Designation</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="designation"
                                            value={filters.designation}
                                            onChange={handleFilterChange}
                                            placeholder="Search by designation"
                                        />
                                    </div>
                                </div>
                                <div className="col-sm-6 col-md-3">
                                    <div className="input-block mb-3">
                                        <label>Status</label>
                                        <select
                                            className="form-control"
                                            name="status"
                                            value={filters.status}
                                            onChange={handleFilterChange}
                                        >
                                            <option value="">All</option>
                                            <option value="true">Active</option>
                                            <option value="false">Inactive</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="col-sm-12">
                                    <div className="input-block mb-3 text-end">
                                        <button type="button" className="btn btn-secondary me-2" onClick={clearFilters}>
                                            Clear Filters
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="row">
                        <div className="col-sm-12">
                            <div className="card-table">
                                <div className="card-body">

                                    {/* Table Controls */}
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <div className="d-flex align-items-center">
                                            <label className="me-2">Show</label>
                                            <select
                                                className="form-select form-select-sm"
                                                style={{ width: 'auto' }}
                                                value={itemsPerPage}
                                                onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                                            >
                                                <option value={10}>10</option>
                                                <option value={25}>25</option>
                                                <option value={50}>50</option>
                                                <option value={100}>100</option>
                                            </select>
                                            <label className="ms-2">entries</label>
                                        </div>
                                        <div>
                                            <label className="me-2">Search:</label>
                                            <input
                                                type="text"
                                                className="form-control form-control-sm d-inline-block"
                                                style={{ width: '200px' }}
                                                placeholder="Search contacts..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="table-responsive">
                                        <table className="table table-center table-hover">
                                            <thead className="thead-light">
                                                <tr>
                                                    <th>#</th>
                                                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('name')}>
                                                        Name{getSortIcon('name')}
                                                    </th>
                                                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('designation')}>
                                                        Designation{getSortIcon('designation')}
                                                    </th>
                                                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('email')}>
                                                        Email{getSortIcon('email')}
                                                    </th>
                                                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('phone')}>
                                                        Phone{getSortIcon('phone')}
                                                    </th>
                                                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('isactive')}>
                                                        Status{getSortIcon('isactive')}
                                                    </th>
                                                    <th className="no-sort">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {loading ? (
                                                    <tr>
                                                        <td colSpan="7" className="text-center">
                                                            <div className="spinner-border" role="status">
                                                                <span className="visually-hidden">Loading...</span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ) : currentContacts.length > 0 ? (
                                                    currentContacts.map((contact, index) => (
                                                        <tr key={contact.id}>
                                                            <td>{startIndex + index + 1}</td>
                                                            <td>
                                                                <span
                                                                    style={{ cursor: 'pointer', color: '#007bff', textDecoration: 'underline' }}
                                                                    onClick={() => handleViewDetails(contact.id)}
                                                                    title="Click to view details"
                                                                >
                                                                    {contact.name}
                                                                </span>
                                                            </td>
                                                            <td>{contact.designation || 'N/A'}</td>
                                                            <td>
                                                                {contact.email
                                                                    ? <a href={`mailto:${contact.email}`} className="text-primary">{contact.email}</a>
                                                                    : 'N/A'}
                                                            </td>
                                                            <td>{contact.phone || 'N/A'}</td>
                                                            <td>
                                                                <span className={`badge ${contact.isactive ? 'bg-success' : 'bg-danger'}`}>
                                                                    {contact.isactive ? 'Active' : 'Inactive'}
                                                                </span>
                                                            </td>
                                                            <td className="d-flex align-items-center">
                                                                <a
                                                                    className="btn-action-icon bg-primary-light me-2"
                                                                    style={{ cursor: 'pointer' }}
                                                                    onClick={() => handleViewDetails(contact.id)}
                                                                    title="View Details"
                                                                >
                                                                    <i className="fa fa-eye"></i>
                                                                </a>
                                                                <a
                                                                    className="btn-action-icon bg-danger-light"
                                                                    style={{ cursor: 'pointer' }}
                                                                    onClick={() => handleDelete(contact.id)}
                                                                    title="Delete"
                                                                >
                                                                    <i className="fa fa-trash-alt"></i>
                                                                </a>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="7" className="text-center">
                                                            {searchTerm ? "No matching contacts found" : "No vendor contacts available"}
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    {totalItems > 0 && <Pagination />}
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
                <Footer />
            </div>
        </div>
    );
}

export default VendorContact;